/**
 * Play-off bracket of a UEFA club competition with a 36-team league phase:
 * knockout play-offs of the places 9-24 (two legs), Round of 16 with the
 * places 1-8 entering (two legs), quarter-finals and semi-finals (two legs),
 * a single final.
 *
 * Slots are named by pairs of league-phase places, not by single places: which
 * team of a pair lands in which slot is decided by a draw each season, so a
 * pair is all that is known before a tie is stored. Each half of the bracket
 * holds one slot of every pair - 1/2 against 7/8 and 3/4 against 5/6 in the
 * quarter-finals - hence the same four slots twice.
 */

import type { BracketRoundTemplate, BracketSideTemplate } from './types';

type Slot = [BracketSideTemplate, BracketSideTemplate];

export const ranks = (...places: number[]): BracketSideTemplate => ({ kind: 'ranks', ranks: places });
const winnerOfRanks = (round: number, ...places: number[]): BracketSideTemplate => ({
  kind: 'winnerOfRanks',
  round,
  ranks: places,
});
export const winnerOfSlot = (round: number, slot: number): BracketSideTemplate => ({ kind: 'winnerOfSlot', round, slot });

const PLAYOFFS_HALF: Slot[] = [
  [ranks(17, 18), ranks(15, 16)],
  [ranks(23, 24), ranks(9, 10)],
  [ranks(19, 20), ranks(13, 14)],
  [ranks(21, 22), ranks(11, 12)],
];

const ROUND_OF_16_HALF: Slot[] = [
  [winnerOfRanks(0, 15, 16, 17, 18), ranks(1, 2)],
  [winnerOfRanks(0, 9, 10, 23, 24), ranks(7, 8)],
  [winnerOfRanks(0, 13, 14, 19, 20), ranks(3, 4)],
  [winnerOfRanks(0, 11, 12, 21, 22), ranks(5, 6)],
];

const QUARTER_FINALS_HALF: Slot[] = [
  [winnerOfRanks(1, 1, 2), winnerOfRanks(1, 7, 8)],
  [winnerOfRanks(1, 3, 4), winnerOfRanks(1, 5, 6)],
];

export const UEFA_LEAGUE_PHASE_BRACKET: BracketRoundTemplate[] = [
  // The name of this round differs between seasons at the source.
  { stage: null, legs: 2, slots: [...PLAYOFFS_HALF, ...PLAYOFFS_HALF] },
  { stage: 'Round of 16', legs: 2, slots: [...ROUND_OF_16_HALF, ...ROUND_OF_16_HALF] },
  { stage: 'Quarter-finals', legs: 2, slots: [...QUARTER_FINALS_HALF, ...QUARTER_FINALS_HALF] },
  {
    stage: 'Semi-finals',
    legs: 2,
    slots: [
      [winnerOfSlot(2, 1), winnerOfSlot(2, 2)],
      [winnerOfSlot(2, 3), winnerOfSlot(2, 4)],
    ],
  },
  { stage: 'Final', legs: 1, slots: [[winnerOfSlot(3, 1), winnerOfSlot(3, 2)]] },
];
